import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Gift, Copy, CheckCircle, ArrowRight, Crown, Share2, Link2, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { referralAPI } from '../lib/api';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function ReferralPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    referralAPI.getStats().then(r => setStats(r.data)).catch(() => toast.error('Failed to load referral data')).finally(() => setLoading(false));
  }, []);

  const referralLink = stats ? `${window.location.origin}/register?ref=${stats.code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join Kairera', text: 'Start your career intelligence journey!', url: referralLink });
      } catch {}
    } else {
      copyLink();
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="referral-loading">
      <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#0D9488', borderTopColor: 'transparent' }} />
    </div>
  );

  const progress = stats ? Math.min(100, (stats.referral_count / stats.threshold) * 100) : 0;

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="referral-page">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="nx-card p-6 text-center" data-testid="referral-hero">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(13,148,136,0.1)' }}>
          <Gift className="w-7 h-7" style={{ color: '#0D9488' }} />
        </div>
        <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>
          Invite Friends, Earn Pro
        </h1>
        <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
          Invite {stats?.threshold || 3} friends and get <strong style={{ color: '#0D9488' }}>1 month of Pro</strong> free.
          Keep inviting for more rewards!
        </p>
      </motion.div>

      {/* Progress */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="nx-card p-5" data-testid="referral-progress">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Your Progress</span>
          <span className="text-sm font-bold" style={{ color: '#0D9488' }}>
            {stats?.referral_count || 0} / {stats?.threshold || 3}
          </span>
        </div>
        <div className="h-3 rounded-full overflow-hidden mb-3" style={{ background: 'var(--t-bg-section)' }}>
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progress}%`, background: progress >= 100 ? '#16A34A' : 'linear-gradient(90deg, #0D9488, #14B8A6)' }} />
        </div>
        <div className="flex justify-between">
          {[...Array(stats?.threshold || 3)].map((_, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1"
                style={{
                  background: i < (stats?.referral_count || 0) ? '#0D9488' : 'var(--t-bg-section)',
                  border: `2px solid ${i < (stats?.referral_count || 0) ? '#0D9488' : 'var(--t-border)'}`,
                }}>
                {i < (stats?.referral_count || 0) ? (
                  <CheckCircle className="w-4 h-4" style={{ color: '#fff' }} />
                ) : (
                  <UserPlus className="w-3 h-3" style={{ color: 'var(--t-text-muted)' }} />
                )}
              </div>
              <span className="text-[9px] font-medium" style={{ color: 'var(--t-text-muted)' }}>Friend {i + 1}</span>
            </div>
          ))}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1"
              style={{
                background: progress >= 100 ? '#16A34A' : 'var(--t-bg-section)',
                border: `2px solid ${progress >= 100 ? '#16A34A' : 'var(--t-border)'}`,
              }}>
              <Crown className="w-4 h-4" style={{ color: progress >= 100 ? '#fff' : 'var(--t-text-muted)' }} />
            </div>
            <span className="text-[9px] font-bold" style={{ color: progress >= 100 ? '#16A34A' : '#0D9488' }}>
              {progress >= 100 ? 'Earned!' : 'Pro'}
            </span>
          </div>
        </div>
        {stats?.rewards_earned > 0 && (
          <div className="mt-3 p-2 rounded-lg text-center text-xs font-bold" style={{ background: 'rgba(22,163,74,0.1)', color: '#16A34A' }}>
            You've earned {stats.rewards_earned} month(s) of Pro!
          </div>
        )}
      </motion.div>

      {/* Referral Link */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="nx-card p-5" data-testid="referral-link-section">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4" style={{ color: '#0D9488' }} />
          <span className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Your Referral Link</span>
        </div>
        <div className="flex gap-2">
          <div className="flex-1 px-3 py-2.5 rounded-lg text-xs font-mono truncate"
            style={{ background: 'var(--t-bg-section)', color: 'var(--t-text-body)', border: '1px solid var(--t-border)' }}
            data-testid="referral-link-display">
            {referralLink}
          </div>
          <Button onClick={copyLink} className="px-4 font-bold text-xs"
            style={{ background: copied ? '#16A34A' : '#0D9488', color: '#fff' }}
            data-testid="copy-referral-btn">
            {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={shareLink} className="flex-1 h-10 font-bold text-xs"
            style={{ background: 'rgba(13,148,136,0.1)', color: '#0D9488' }}
            data-testid="share-referral-btn">
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share Link
          </Button>
          <Button onClick={copyLink} className="flex-1 h-10 font-bold text-xs"
            style={{ background: 'var(--t-bg-section)', color: 'var(--t-text-body)' }}>
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Code: {stats?.code}
          </Button>
        </div>
      </motion.div>

      {/* Referred Users */}
      {stats?.referred_users?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="nx-card p-5" data-testid="referral-users-list">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4" style={{ color: '#0D9488' }} />
            <span className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Your Referrals</span>
          </div>
          <div className="space-y-2">
            {stats.referred_users.map((u, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg"
                style={{ background: 'var(--t-bg-section)' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold"
                  style={{ background: 'rgba(13,148,136,0.15)', color: '#0D9488' }}>
                  {u.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold" style={{ color: 'var(--t-text-heading)' }}>{u.name}</p>
                  <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                    {u.date ? new Date(u.date).toLocaleDateString() : ''}
                  </p>
                </div>
                <CheckCircle className="w-4 h-4" style={{ color: '#16A34A' }} />
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
